import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import Warteliste from '@/types/Warteliste';
import api from '@/services/Api';
import { successSwal, warningSwal } from '@/helper/swals';
import {WartelisteState, RootState, WartelisteFilter, PatientState} from '@/store/types';
import Interval from '@/helper/Interval';
import {SystemEinstellung, SystemEinstellungKey} from '@/types/Einstellung';

const WARTELISTE_REFRESH_SECONDS = 30;

export type LoadWarteListePayload = WartelisteFilter;
export type UpdateWartelistePayload = { kassa?: boolean, leistungen?: number[] };

type Context = ActionContext<WartelisteState, RootState>;

export const warteliste: Module<WartelisteState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
        },
        loaders: {
            finish: false,
            delete: false,
            warteliste: false,
            update: false,
            kassa: false,
        },
        warteliste: [],
        filter: {
            fertig: false,
        },
        refresh: null,
        wartelisteItem: new Warteliste()
    },
    actions: {
        loadWarteliste({ commit, state, dispatch, rootState, rootGetters }: Context, payload?: LoadWarteListePayload): void {
            if (state.loaders.warteliste) {
                return;
            }

            commit('setLoaders', { warteliste: true });

            if (payload) {
                commit('setFilter', payload);
            }

           /* const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            if (getSystemEinstellung('etikettvariante').value === 'Stemberger' && state.filter.fertig === null) {
                state.filter.fertig = true;
                commit('setFilter', {fertig: true});
            }*/

            const data = {
                ...state.filter,
                ...payload,
                impfstelle_id: rootState.authentication.impfstelle_id,
            };

            api.post('ivs/warteliste', data)
                .then((response) => commit('setWarteliste', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoaders', { warteliste: false }));

            if (!state.refresh) {
                state.refresh = new Interval(WARTELISTE_REFRESH_SECONDS * 1000, () => dispatch('loadWarteliste'));
                state.refresh.start();
            }
        },
        updateWarteliste({ commit, dispatch, rootState }: Context, payload?: UpdateWartelistePayload): void {
            const patnr = rootState.patient.patientData?.patnr;
            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const impfstelle_id = rootState.authentication.impfstelle_id;

            if (!patnr) {
                return;
            }

            if (!arbeitsplatz_id) {
                setTimeout(() => {
                    commit('globals/setDialogArbeitsplatzhinweis', true, { root: true });
                    commit('globals/setDialogArbeitsplatzhinweisSource', 'warteliste', { root: true });
                }, 100);
                return;
            }

            commit('setLoaders', { [payload?.kassa ? 'kassa' : 'update']: true });

            const data = {
                patnr,
                arbeitsplatz_id,
                impfstelle_id,
                ...payload,
            };

            api.post('ivs/warteliste/update', data)
                .then(() => {
                    dispatch('loadWarteliste');
                    dispatch('patient/loadImpfungen', null, { root: true });
                    commit('patient/setDialogZurKassa', false, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { [payload?.kassa ? 'kassa' : 'update']: false }));
        },
        finishWarteliste({ commit, dispatch, rootState }: Context, payload: { warteliste: number[] }): void {
            commit('setLoaders', { finish: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('ivs/warteliste/finish', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Warteliste', response.data.body ?? 'Fehler beim Speichern.');
                    } else {
                        successSwal('Warteliste', 'gespeichert');
                    }

                    dispatch('loadWarteliste');
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { finish: false }));
        },
        updateWartelisteItems({ commit, dispatch, rootState }: Context, payload: { wartelisteitems: Warteliste[], einblenden: boolean }): void {

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('ivs/warteliste/updatewartelisteitems', data)
                .then((response: AxiosResponse) => {
                    dispatch('loadWarteliste');
                })
                .catch(console.error)
        },
        deleteWarteliste({ commit, dispatch, rootState }: Context, payload: { warteliste: number[] }): void {
            commit('setLoaders', { delete: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('ivs/warteliste/delete', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Warteliste', response.data.body ?? 'Fehler beim Löschen.');
                    } else {
                        successSwal('Warteliste', 'gelöscht');
                    }

                    dispatch('loadWarteliste');
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { delete: false }));
        },
    },
    mutations: {
        setWarteliste(state: WartelisteState, payload: Partial<Warteliste>[]): void {
            state.warteliste = payload.map((data) => new Warteliste(data));
            state.warteliste.forEach((item) => item.calcWaitTime());
        },
        setDialogs(state: WartelisteState, dialogs: { edit?: boolean }): void {
            state.dialogs = {
                ...state.dialogs,
                ...dialogs,
            };
        },
        setLoaders(state: WartelisteState,
                   loaders: { finish?: boolean; delete?: boolean; warteliste?: boolean }): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
        setFilter(state: WartelisteState, filter: WartelisteFilter): void {
            state.filter = {
                ...filter,
            };
        },
        setWartelisteItem(state: WartelisteState, payload: Warteliste): void {
            state.wartelisteItem = payload;
        },
        setEdit(state: WartelisteState, payload: boolean): void {
            state.dialogs.edit = payload;
        },
    },
};
